import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTheme } from '@mui/material';

const LocationForm = ({ 
  open, 
  onClose, 
  onSave, 
  location = null, 
  isEdit = false 
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    partyId: '',
    countryCode: '',
    status: 'ACTIVE',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    coordinates: {
      latitude: '',
      longitude: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location && isEdit) {
      setFormData({
        name: location.name || '',
        partyId: location.partyId || '',
        countryCode: location.countryCode || '',
        status: location.status || 'ACTIVE',
        address: {
          street: location.address?.street || '',
          city: location.address?.city || '',
          postalCode: location.address?.postalCode || '',
          country: location.address?.country || ''
        },
        coordinates: {
          latitude: location.coordinates?.latitude || '',
          longitude: location.coordinates?.longitude || ''
        }
      });
    }
  }, [location, isEdit]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    }

    if (!formData.partyId.trim()) {
      newErrors.partyId = 'Party ID is required';
    }

    if (!formData.countryCode.trim()) {
      newErrors.countryCode = 'Country code is required';
    }

    if (!formData.address.street.trim()) {
      newErrors['address.street'] = 'Street address is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.country.trim()) {
      newErrors['address.country'] = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      partyId: '',
      countryCode: '',
      status: 'ACTIVE',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: ''
      },
      coordinates: {
        latitude: '',
        longitude: ''
      }
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Icon 
            icon={isEdit ? "eva:edit-fill" : "eva:plus-fill"} 
            style={{ fontSize: '24px', color: theme.palette.primary.main }} 
          />
          <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            {isEdit ? 'Edit Location' : 'Add New Location'}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '20',
              color: theme.palette.primary.dark
            }
          }}
        >
          <Icon icon="eva:close-fill" fontSize={20} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Party ID"
              value={formData.partyId}
              onChange={(e) => handleInputChange('partyId', e.target.value)}
              error={!!errors.partyId}
              helperText={errors.partyId}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country Code"
              value={formData.countryCode}
              onChange={(e) => handleInputChange('countryCode', e.target.value)}
              error={!!errors.countryCode}
              helperText={errors.countryCode}
              placeholder="e.g., US, CA, UK"
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.info.main }}>
              Address Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={formData.address.street}
              onChange={(e) => handleInputChange('address.street', e.target.value)}
              error={!!errors['address.street']}
              helperText={errors['address.street']}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.address.city}
              onChange={(e) => handleInputChange('address.city', e.target.value)}
              error={!!errors['address.city']}
              helperText={errors['address.city']}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.address.postalCode}
              onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Country"
              value={formData.address.country}
              onChange={(e) => handleInputChange('address.country', e.target.value)}
              error={!!errors['address.country']}
              helperText={errors['address.country']}
              required
            />
          </Grid>

          {/* Coordinates */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.success.main }}>
              Geographic Coordinates (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={formData.coordinates.latitude}
              onChange={(e) => handleInputChange('coordinates.latitude', e.target.value)}
              placeholder="e.g., 40.7128"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={formData.coordinates.longitude}
              onChange={(e) => handleInputChange('coordinates.longitude', e.target.value)}
              placeholder="e.g., -74.0060"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={<Icon icon={isSubmitting ? "eva:loader-fill" : (isEdit ? "eva:save-fill" : "eva:plus-fill")} />}
        >
          {isSubmitting ? 'Saving...' : (isEdit ? 'Update Location' : 'Create Location')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationForm;
